import { format } from 'date-fns';
import { BANNER_LOCATION_OPTIONS } from './banners.data';

export const BANNER_SIMPLE_TYPE = 652;

const LOCATION_ALIASES = {
  deals: ['deals'],
  home: ['home'],
  menu: ['menu'],
  payments: ['payments'],
};

const LOCATION_CODE_MAP = BANNER_LOCATION_OPTIONS.reduce((acc, option) => {
  acc[String(option.code)] = option.value;
  return acc;
}, {});

const normalizeText = value =>
  String(value || '')
    .trim()
    .toLowerCase();

export const normalizeTimeValue = value =>
  value
    ? String(value)
        .trim()
        .substring(0, 5)
    : '';

export const normalizeLocationValue = value => {
  const candidate =
    typeof value === 'object'
      ? value?.value ||
        value?.key ||
        value?.location ||
        value?.name ||
        value?.code ||
        ''
      : value;
  const codeMappedCandidate = LOCATION_CODE_MAP[String(candidate)];

  if (codeMappedCandidate) {
    return codeMappedCandidate;
  }
  const normalizedCandidate = normalizeText(candidate);

  return (
    Object.keys(LOCATION_ALIASES).find(key =>
      LOCATION_ALIASES[key].includes(normalizedCandidate)
    ) || candidate
  );
};

export const mapLocationLabel = value => {
  const normalizedValue = normalizeLocationValue(value);
  const option = BANNER_LOCATION_OPTIONS.find(
    location => location.value === normalizedValue
  );

  return option ? option.label : value;
};

export const getAssignedLocations = banner =>
  (
    banner?.show_location ||
    banner?.placements ||
    banner?.banner_placements ||
    banner?.locations ||
    []
  )
    .map(normalizeLocationValue)
    .filter(Boolean);

export const hasHomePageLocation = banner =>
  getAssignedLocations(banner).includes('home');

const getActivationFlag = banner =>
  Boolean(
    (banner?.status !== undefined ? Number(banner.status) === 1 : undefined) ??
      banner?.active ??
      banner?.is_active ??
      banner?.activated ??
      banner?.enabled ??
      banner?.published_at
  );

const getDateInterval = banner => {
  if (banner?.date_interval?.length === 2) return banner.date_interval;

  return [
    banner?.published_at || banner?.valid_from || banner?.starts_at,
    banner?.unpublished_at || banner?.valid_until || banner?.ends_at,
  ];
};

const buildSchedule = ({ dateFrom, dateTo, timeFrom, timeTo }) => {
  const start =
    dateFrom && timeFrom ? new Date(`${dateFrom}T${timeFrom}`) : null;
  const end = dateTo && timeTo ? new Date(`${dateTo}T${timeTo}`) : null;

  return { start, end };
};

export const getBannerSchedule = banner => {
  const [dateFromRaw, dateToRaw] = getDateInterval(banner);
  const timeFrom =
    banner?.time_interval?.length === 2
      ? normalizeTimeValue(banner.time_interval[0])
      : dateFromRaw
      ? format(new Date(dateFromRaw), 'HH:mm')
      : '';
  const timeTo =
    banner?.time_interval?.length === 2
      ? normalizeTimeValue(banner.time_interval[1])
      : dateToRaw
      ? format(new Date(dateToRaw), 'HH:mm')
      : '';

  const dateFrom = dateFromRaw
    ? format(new Date(dateFromRaw), 'yyyy-MM-dd')
    : '';
  const dateTo = dateToRaw ? format(new Date(dateToRaw), 'yyyy-MM-dd') : '';

  return {
    dateFrom,
    dateTo,
    timeFrom,
    timeTo,
    ...buildSchedule({ dateFrom, dateTo, timeFrom, timeTo }),
  };
};

export const isBannerPastSchedule = bannerLike => {
  const schedule =
    bannerLike?.start || bannerLike?.end
      ? bannerLike
      : buildSchedule(bannerLike);

  return Boolean(schedule.end && schedule.end.getTime() < Date.now());
};

export const getBannerStatus = banner => {
  const { start, end } = getBannerSchedule(banner);
  const active = getActivationFlag(banner);
  const now = Date.now();
  const withinSchedule =
    (!start || start.getTime() <= now) && (!end || end.getTime() >= now);
  const isActive = active && withinSchedule;

  return {
    isActive,
    label: isActive ? 'Galioja' : 'Negalioja',
    color: isActive ? '#24B34B' : '#F04438',
  };
};

export const getDefaultBannerValues = currentDate => ({
  title: '',
  isActivated: false,
  hasDiscount: false,
  discountId: '',
  deepLink: '',
  dateFrom: format(currentDate || Date.now(), 'yyyy-MM-dd'),
  dateTo: format(currentDate || Date.now(), 'yyyy-MM-dd'),
  timeFrom: '',
  timeTo: '',
  placements: [],
  image: null,
});

export const normalizeBannerValues = banner => {
  const schedule = getBannerSchedule(banner);
  const image = banner?.banner || banner?.image || null;

  return {
    title:
      banner?.title_lt ||
      banner?.title_en ||
      banner?.title_ru ||
      banner?.title ||
      banner?.name ||
      '',
    isActivated: getActivationFlag(banner),
    hasDiscount: Boolean(banner?.discount_id || banner?.discount?.id),
    discountId: banner?.discount_id || banner?.discount?.id || '',
    deepLink: banner?.deep_link || '',
    dateFrom: schedule.dateFrom,
    dateTo: schedule.dateTo,
    timeFrom: isAllDayTimeRange(schedule.timeFrom, schedule.timeTo)
      ? ''
      : schedule.timeFrom,
    timeTo: isAllDayTimeRange(schedule.timeFrom, schedule.timeTo)
      ? ''
      : schedule.timeTo,
    placements: getAssignedLocations(banner),
    image,
  };
};

export const getBannerFieldsFromDiscount = discount => {
  if (!discount) return null;

  const dateFromRaw = discount.start_date || discount.published_at;
  const dateToRaw = discount.end_date || discount.unpublished_at;
  const timeFromRaw =
    discount.valid_time_from || discount?.time_interval?.[0] || '';
  const timeToRaw =
    discount.valid_time_to || discount?.time_interval?.[1] || '';

  return {
    title: discount.description_lt || discount.code || '',
    dateFrom: dateFromRaw ? format(new Date(dateFromRaw), 'yyyy-MM-dd') : '',
    dateTo: dateToRaw ? format(new Date(dateToRaw), 'yyyy-MM-dd') : '',
    timeFrom: normalizeTimeValue(timeFromRaw),
    timeTo: normalizeTimeValue(timeToRaw),
  };
};

export const isAllDayTimeRange = (timeFrom, timeTo) =>
  (!timeFrom && !timeTo) || (timeFrom === '00:00' && timeTo === '23:59');

export const getBannerTimeRange = values => ({
  timeFrom: values?.timeFrom || '00:00',
  timeTo: values?.timeTo || '23:59',
});

export const createBannerPayload = values => {
  const { timeFrom, timeTo } = getBannerTimeRange(values);
  const dateFrom = new Date(`${values.dateFrom}T${timeFrom}`).toISOString();
  const dateTo = new Date(`${values.dateTo}T${timeTo}`).toISOString();
  const showLocation = values.placements.map(
    placement =>
      BANNER_LOCATION_OPTIONS.find(option => option.value === placement)?.code
  );

  return {
    type: BANNER_SIMPLE_TYPE,
    title_lt: values.title,
    title_en: values.title,
    title_ru: values.title,
    description_lt: values.title,
    description_en: values.title,
    description_ru: values.title,
    status: values.isActivated ? 1 : 0,
    published_at: dateFrom,
    unpublished_at: dateTo,
    show_location: showLocation.filter(Boolean),
    discount_id: values.hasDiscount ? Number(values.discountId) : null,
    deep_link: values.hasDiscount ? null : values.deepLink,
    app_notification: false,
    app_notification_send_at: null,
  };
};
